import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { mangaList, rankingData, categories } from '@/mocks/mangaData';

const featuredManga = mangaList.filter((m) =>
  ['jujutsu-kaisen', 'one-piece', 'demon-slayer', 'attack-titan', 'frieren'].includes(m.id)
);

const latestManga = [...mangaList].sort(
  (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
).slice(0, 8);

function StarRating({ rating }: { rating: number }) {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <i
          key={i}
          className={`text-xs ${
            i < full
              ? 'ri-star-fill text-accent-500'
              : i === full && half
              ? 'ri-star-half-fill text-accent-500'
              : 'ri-star-fill text-background-300'
          }`}
        ></i>
      ))}
      <span className="ml-1 text-xs font-semibold text-foreground-700">{rating}</span>
    </div>
  );
}

function MangaCard({ manga, size = 'normal' }: { manga: typeof mangaList[0]; size?: 'large' | 'normal' }) {
  return (
    <Link
      to={`/article/${manga.id}-ch${manga.chapterCount}`}
      className="group block"
    >
      <div
        className={`relative overflow-hidden rounded-lg bg-background-100 ${
          size === 'large' ? 'aspect-[3/4]' : 'aspect-[3/4]'
        }`}
      >
        <img
          src={manga.cover}
          alt={manga.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          title={manga.title}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-3">
          {size === 'large' && (
            <span className="inline-block px-2 py-0.5 rounded-md bg-primary-500 text-white text-xs font-medium mb-2">
              {manga.status}
            </span>
          )}
          <h3 className="text-sm font-semibold text-white line-clamp-1 font-heading">
            {manga.title}
          </h3>
          {size === 'large' && (
            <p className="text-xs text-white/70 mt-1 line-clamp-2">{manga.description}</p>
          )}
        </div>
        <div className="absolute top-2 right-2">
          <div className="px-2 py-0.5 rounded-md bg-black/60 backdrop-blur-sm text-white text-xs font-medium">
            {manga.chapterCount}话
          </div>
        </div>
      </div>
      {size !== 'large' && (
        <div className="mt-2">
          <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
            {manga.title}
          </h3>
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs text-foreground-500">{manga.author}</span>
            <StarRating rating={manga.rating} />
          </div>
        </div>
      )}
    </Link>
  );
}

export default function Home() {
  const homepageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '36漫画网',
    url: 'https://36manhua.com',
    description: '36漫画网为您呈现咒术回战漫画全集无删减版，在线免费阅读，所有章节一网打尽，享受原汁原味的完整故事，不容错过。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://36manhua.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '36漫画网',
    url: 'https://36manhua.com',
    description: '专业漫画在线阅读平台，提供咒术回战等热门漫画全集无删减免费阅读',
    sameAs: ['https://weibo.com/36manhua', 'https://weixin.qq.com/36manhua'],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homepageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Japanese%20manga%20style%20dark%20fantasy%20illustration%2C%20abstract%20cursed%20energy%20flowing%20with%20crimson%20and%20black%20tones%2C%20traditional%20Japanese%20brush%20stroke%20texture%2C%20dramatic%20atmospheric%20composition%2C%20minimalist%20cinematic%20lighting&width=1800&height=900&seq=hero-bg-manga&orientation=landscape"
            alt="咒术回战漫画"
            className="absolute inset-0 w-full h-full object-cover object-top"
            title="咒术回战漫画在线免费阅读"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="relative z-10 w-full px-4 md:px-6 max-w-5xl mx-auto text-center">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-500/90 text-white text-xs font-medium mb-4">
              热门推荐
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              咒术回战漫画
              <br />
              <span className="text-primary-300">在线免费阅读全集无删减</span>
            </h1>
            <p className="mt-4 text-sm md:text-base text-white/80 max-w-2xl mx-auto leading-relaxed">
              36漫画网为您呈现咒术回战漫画全集无删减版，在线免费阅读，所有章节一网打尽，
              享受原汁原味的完整故事，不容错过。
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/article/jjk-ch271"
                className="px-8 py-3 rounded-lg bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-play-circle-line mr-2"></i>
                立即阅读
              </Link>
              <Link
                to="/category/shaonian"
                className="px-8 py-3 rounded-lg bg-white/10 backdrop-blur-sm text-white font-medium text-sm border border-white/20 hover:bg-white/20 transition-colors whitespace-nowrap"
              >
                浏览分类
              </Link>
            </div>
            <div className="mt-12 flex items-center justify-center gap-8">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white font-label">12+</div>
                <div className="text-xs text-white/60 mt-1">热门漫画</div>
              </div>
              <div className="w-px h-10 bg-white/20"></div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white font-label">3000+</div>
                <div className="text-xs text-white/60 mt-1">漫画章节</div>
              </div>
              <div className="w-px h-10 bg-white/20"></div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white font-label">100万+</div>
                <div className="text-xs text-white/60 mt-1">忠实读者</div>
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                漫画分类
              </h2>
              <Link
                to="/category/shaonian"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-3 p-5 rounded-xl bg-background-100 hover:bg-primary-50 transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                    <i className={`${
                      cat.slug === 'shaonian' ? 'ri-sword-line' :
                      cat.slug === 'shaonv' ? 'ri-heart-line' :
                      cat.slug === 'qingnian' ? 'ri-user-star-line' :
                      cat.slug === 'rexue' ? 'ri-fire-line' :
                      cat.slug === 'kehuan' ? 'ri-rocket-line' :
                      cat.slug === 'xuanhuan' ? 'ri-magic-line' :
                      cat.slug === 'kongbu' ? 'ri-ghost-line' :
                      cat.slug === 'gaoxiao' ? 'ri-emotion-laugh-line' :
                      cat.slug === 'yundong' ? 'ri-basketball-line' :
                      'ri-heart-2-line'
                    } text-xl text-primary-500`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Manga */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                热门推荐
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {featuredManga.map((manga) => (
                <MangaCard key={manga.id} manga={manga} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates + Ranking */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Latest Updates */}
              <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                    最新更新
                  </h2>
                  <Link
                    to="/category/shaonian"
                    className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1"
                  >
                    更多 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {latestManga.map((manga) => (
                    <MangaCard key={manga.id} manga={manga} />
                  ))}
                </div>
              </div>

              {/* Ranking */}
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                    排行榜
                  </h2>
                  <Link
                    to="/category/shaonian"
                    className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1"
                  >
                    完整榜单 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="bg-background-100 rounded-xl p-4">
                  {rankingData.map((item) => {
                    const manga = mangaList.find((m) => m.id === item.mangaId);
                    return (
                      <Link
                        key={item.rank}
                        to={`/article/${item.mangaId}-ch${manga?.chapterCount || 1}`}
                        className="flex items-center gap-3 py-3 border-b border-background-200/70 last:border-0 hover:bg-background-50/50 rounded-lg px-2 -mx-2 transition-colors group"
                      >
                        <span
                          className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold font-label ${
                            item.rank <= 3
                              ? 'bg-primary-500 text-white'
                              : 'bg-background-200 text-foreground-600'
                          }`}
                        >
                          {item.rank}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
                            {item.title}
                          </p>
                          <p className="text-xs text-foreground-500 mt-0.5">
                            {(item.views / 10000).toFixed(0)}万阅读
                          </p>
                        </div>
                        <span className="text-xs">
                          {item.trend === 'up' ? (
                            <i className="ri-arrow-up-line text-green-500"></i>
                          ) : item.trend === 'down' ? (
                            <i className="ri-arrow-down-line text-red-500"></i>
                          ) : (
                            <i className="ri-subtract-line text-foreground-400"></i>
                          )}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About Teaser */}
        <section className="py-14 md:py-20 bg-background-950">
          <div className="px-4 md:px-6 max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">
              为什么选择36漫画网？
            </h2>
            <p className="mt-4 text-sm text-background-400 leading-relaxed">
              我们致力于为漫画爱好者提供最佳阅读体验。全集无删减、高清画质、更新及时，
              让每一位读者都能享受到原汁原味的漫画故事。
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="p-6 rounded-xl bg-background-900/50 border border-background-800">
                <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-book-open-line text-2xl text-primary-400"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-white mb-2">全集无删减</h3>
                <p className="text-xs text-background-400 leading-relaxed">所有漫画章节完整收录，无删减、无遗漏，还原最真实的故事内容。</p>
              </div>
              <div className="p-6 rounded-xl bg-background-900/50 border border-background-800">
                <div className="w-12 h-12 rounded-xl bg-accent-500/20 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-flashlight-line text-2xl text-accent-400"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-white mb-2">极速更新</h3>
                <p className="text-xs text-background-400 leading-relaxed">同步日本连载进度，第一时间更新最新章节，追更绝不落后。</p>
              </div>
              <div className="p-6 rounded-xl bg-background-900/50 border border-background-800">
                <div className="w-12 h-12 rounded-xl bg-secondary-500/20 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-2xl text-secondary-400"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-white mb-2">完全免费</h3>
                <p className="text-xs text-background-400 leading-relaxed">全部内容免费阅读，无需付费、无需会员，让每个人都能畅享漫画乐趣。</p>
              </div>
            </div>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 mt-10 px-8 py-3 rounded-lg bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              了解更多 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}